import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../../api/config";
import KuendigenModal from "./KuendigenModal";

const API = `${API_BASE}/mietverwaltung/api/kuendigung`;

async function blobDownload(res, fallback) {
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m2 = cd.match(/filename\*=UTF-8''([^;]+)/);
  const m1 = cd.match(/filename="([^"]+)"/);
  const name = m2 ? decodeURIComponent(m2[1]) : (m1 ? m1[1] : fallback);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  return name;
}

/**
 * Schreiben vom Vertrag aus erzeugen (Tab Dokumente, Welle 82/83):
 * Kündigungsschreiben, Kündigungsbestätigung, Übergabeprotokoll, Kautionsabrechnung.
 * Props: mietvertragNr, vertragName, typ, beschreibung
 */
export default function SchreibenPanel({ mietvertragNr, vertragName, typ, beschreibung }) {
  const navigate = useNavigate();
  const [vs, setVs] = useState(null);
  const [text, setText] = useState("");
  const [fehler, setFehler] = useState("");
  const [brauchtVorgang, setBrauchtVorgang] = useState(false);
  const [busy, setBusy] = useState(false);
  const [meldung, setMeldung] = useState("");
  const [kuendigen, setKuendigen] = useState(false);

  const laden = useCallback(async () => {
    if (!mietvertragNr) { setVs(null); return; }
    setFehler(""); setMeldung(""); setBrauchtVorgang(false);
    try {
      const res = await fetch(`${API}/vertrag-schreiben/vorschau?mietvertrag_nr=${encodeURIComponent(mietvertragNr)}&typ=${typ}`);
      const d = await res.json();
      if (res.status === 409) { setBrauchtVorgang(true); setFehler(d.detail || ""); setVs(null); return; }
      if (!res.ok) throw new Error(d.detail || "Vorschau fehlgeschlagen");
      setVs(d); setText(d.text_gerendert || "");
    } catch (e) { setFehler(String(e.message || e)); setVs(null); }
  }, [mietvertragNr, typ]);
  useEffect(() => { laden(); }, [laden]);

  const erzeugen = async (nurVorschau) => {
    setBusy(true); setFehler("");
    try {
      const res = await fetch(`${API}/vertrag-schreiben?mietvertrag_nr=${encodeURIComponent(mietvertragNr)}&typ=${typ}&nur_vorschau=${nurVorschau ? 1 : 0}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text_override: text }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "PDF fehlgeschlagen");
      const name = await blobDownload(res, `${vs?.titel || typ} - ${mietvertragNr}.pdf`);
      if (!nurVorschau) {
        const pfad = res.headers.get("X-Ablage-Pfad");
        const quelle = res.headers.get("X-Ablage-Quelle");
        setMeldung(pfad ? `„${name}“ erzeugt und abgelegt: ${decodeURIComponent(pfad)}${quelle === "fallback" ? " (Sammelordner — kein Vertragsordner gefunden)" : ""}`
          : `„${name}“ erzeugt (keine Dropbox-Ablage: ${quelle}).`);
      }
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  if (!mietvertragNr) {
    return <div className="mb-hinweis">Bitte oben Objekt und Mietvertrag wählen.</div>;
  }

  return (
    <div className="mb-schreiben">
      <div className="mb-hinweis">{beschreibung}</div>
      {brauchtVorgang && (
        <div className="mb-meldung fehler" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <span>{fehler || "Dafür wird ein Kündigungsvorgang benötigt."}</span>
          <button className="mb-btn primary" onClick={() => setKuendigen(true)}>Kündigungsvorgang starten</button>
          <button className="mb-btn" onClick={() => navigate("/mietverwaltung?tab=vorgaenge")}>Zu den Vorgängen</button>
        </div>
      )}
      {!brauchtVorgang && fehler && <div className="mb-meldung fehler">{fehler}</div>}
      {meldung && <div className="mb-meldung ok">{meldung}</div>}
      {vs && (
        <>
          <div className="mb-schreiben-meta">
            <div><b>Absender</b><br />{vs.absender?.name}<br />{vs.absender?.anschrift}</div>
            <div><b>Empfänger</b><br />{(vs.empfaenger || []).map((z, i) => <span key={i}>{z}<br /></span>)}</div>
            <div><b>Ablage</b><br />{vs.ablage?.pfad || "keine Dropbox"}{vs.ablage?.quelle === "fallback" ? <><br /><small>Sammelordner — {vs.ablage.grund}</small></> : null}
              {vs.vorgang_id ? <><br /><small>Kündigungsvorgang #{vs.vorgang_id} ({vs.vorgang_status})</small></> : <><br /><small>ohne Kündigungsvorgang</small></>}</div>
          </div>
          <label className="mb-feld" style={{ marginTop: 10 }}>
            <span className="mb-label">Betreff</span>
            <input className="mb-input" value={vs.betreff || ""} readOnly />
          </label>
          <label className="mb-feld" style={{ marginTop: 10 }}>
            <span className="mb-label">Text — anpassbar (**fett**, Aufzählung mit „- “, Platzhalter werden beim Erzeugen gefüllt)</span>
            <textarea className="mb-input mb-textarea" value={text} onChange={(e) => setText(e.target.value)} />
          </label>
          {vs.platzhalter_fehlend?.length > 0 && (
            <div className="mb-meldung fehler">Fehlende Angaben: {vs.platzhalter_fehlend.join(", ")} — bitte im Text ersetzen oder in den Stammdaten pflegen.</div>
          )}
          <div className="mb-buttons">
            <button className="mb-btn" disabled={busy} onClick={() => setText(vs.text_gerendert || "")}>Text zurücksetzen</button>
            <button className="mb-btn" disabled={busy} onClick={() => erzeugen(true)}>PDF-Vorschau</button>
            <button className="mb-btn primary" disabled={busy} onClick={() => erzeugen(false)}>{busy ? "Erzeugt…" : "PDF erzeugen + in Dropbox ablegen"}</button>
          </div>
        </>
      )}
      {kuendigen && (
        <KuendigenModal vertrag={{ mietvertrag_nr: mietvertragNr, name: vertragName }} onClose={() => setKuendigen(false)}
          onCreated={(vorgang) => { setKuendigen(false); navigate(`/mietverwaltung?tab=vorgaenge&vorgang=${vorgang.id}`); }} />
      )}
    </div>
  );
}
